import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CompareHelper, ConvertHelper, DataUrl, FileHelper } from '@barthauer/abc/core';
import { PersistenceService } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  AbcContainerService,
  AbcDatepickerComponent,
  AbcLoaderComponent,
  AbcWindowComponent,
  ILoader,
  IWindow,
  WindowStyle,
  AbcMediaGalleryComponent,
  MediaDocument,
  DataChangedEventArgs,
  DataChangedType,
} from '@barthauer/abc/ui-components';
import {
  BasysMapFrameService,
  GeometryEditType,
  PanelComponent,
  ViewStateChangeArgType,
} from '@barthauer/basys/map-frame';
import { resultDetailExpand } from '../../animations';
import { AppService } from '../../services/app.service';
import { FrameService } from '../../services/frame.service';
import { OkDialogComponent } from '../../shared/ok-dialog.component';
import { IMobileResult, MobileResultViewModel } from '../../viewModels/mobileData.viewModel';
import { WorkConstants, WorkNavigationContext, WorkTypeDurationMode } from '../works.model';
import { WorksService } from '../works.service';
import { MTHelper } from '../../shared/helpers';
import { MobileDocumentViewModel } from '../../viewModels/mobileData.common.viewModel';
import { LoadProtocolTaskInfoSnapshot } from '@barthauer/abc/messages';
import { DocumentKind } from '../../models/mtData.model';
import { GeometryEditorComponent } from 'libs/basys/map-frame/src/lib/manipulation/geometry-editor/geometry-editor.component';

@Component({
  selector: 'app-task-detail',
  templateUrl: './task-detail.component.html',
  styleUrls: ['./task-detail.component.scss'],
  animations: [resultDetailExpand],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class TaskDetailComponent extends PanelComponent implements OnInit, OnDestroy {
  viewContainerRef: ViewContainerRef;
  @ViewChild('messaegBoxContainer', { read: ViewContainerRef })
  messaegBoxContainer: ViewContainerRef;
  @ViewChild('abcLoaderContainer', { static: true, read: ViewContainerRef })
  abcLoaderContainerRef: ViewContainerRef;
  @ViewChild('abc', { static: true }) abc: AbcDatepickerComponent;
  @ViewChild('abcWindowContainer', { read: ViewContainerRef })
  abcWindow: ViewContainerRef;
  @ViewChild(AbcMediaGalleryComponent) mediaGallery: AbcMediaGalleryComponent;
  private abcLoader: AbcLoaderComponent;
  private selection: SelectionModel<IMobileResult>;
  private openGeometryEditor: boolean = false;
  public mediaDocuments: MediaDocument[] = [];
  public pointQuantity: number = 0;
  public lineQuantity: number = 0;
  public polygonQuantity: number = 0;
  public categoryDataSources: {} = {};
  private designationsMap = new Map<MobileResultViewModel, string>();
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  public needForPersistence: boolean;
  private _expandedElement: IMobileResult | null;
  public displayedColumns: string[] = ['select', 'designation'];
  readonly VIEWPORTHEIGHTVERTICALMARGIN_DEFAULT: number = 150;
  public scrollViewPortElementName: string = 'scrollArea';
  public workNavigationContext: WorkNavigationContext;
  private isOkDialogOpened: boolean;
  private uploadFileDocumentsTaskInfos: Map<MobileDocumentViewModel, LoadProtocolTaskInfoSnapshot>;
  private uploadFilesCache: Map<File, string>;

  public get scrollViewPortHeightVerticalMargin(): number {
    return this.VIEWPORTHEIGHTVERTICALMARGIN_DEFAULT;
  }

  public get expandedElement(): IMobileResult | null {
    return this._expandedElement;
  }

  public set expandedElement(value: IMobileResult | null) {
    if (this._expandedElement !== value) {
      this._expandedElement = value;
      if (value) {
        this.loadMediaDocuments(value as MobileResultViewModel);
      }
    }
  }
  public get expandedResult(): MobileResultViewModel {
    return this._expandedElement as MobileResultViewModel;
  }

  constructor(
    private persistenceService: PersistenceService,
    frame: BasysMapFrameService,
    private worksService: WorksService,
    public appService: AppService,
    private resourceService: ResourceManagerService,
    private abcContainer: AbcContainerService,
    private zone: NgZone,
    private changeDetectorRef: ChangeDetectorRef,
    private frameService: FrameService,
    private okDialog: MatDialog,
  ) {
    super();
    super.init(frame);

    this.resAsync = this.resourceService.getResourceSet('abc-maintenance-opera');
    this.uploadFileDocumentsTaskInfos = new Map<MobileDocumentViewModel, LoadProtocolTaskInfoSnapshot>();
    this.uploadFilesCache = new Map<File, string>();
    this.subscriptions.push(
      this.worksService.onNavigationContextRefresh().subscribe((context) => {
        this.workNavigationContext = context;
        this.categoryDataSources = {};
        this.designationsMap = new Map();
        const task = this.workNavigationContext.task;

        this.zone.run(() => {
          this.pointQuantity = task.geometry?.Points?.length;
          this.lineQuantity = task.geometry?.Lines?.length;
          this.polygonQuantity = task.geometry?.Polygons?.length;
        });

        let resultCategories = Object.keys(task.resultCategories).sort();
        const sortOrdersMap = new Map<MobileResultViewModel, number>();
        const sortOrdersCategoryMap = new Map<string, number>();
        for (const resultCategory of resultCategories) {
          const data = task.resultCategories[resultCategory] as MobileResultViewModel[];

          if (data) {
            let minSortOrder = 0;
            for (const r of data) {
              this.designationsMap.set(r, r.mtResultType?.Designation ?? task.getDesignation(r.mtResultType));
              const sortOrder = task.getSortOrder(r.mtResultType);
              if (sortOrder) {
                sortOrdersMap.set(r, sortOrder);
                if (!minSortOrder || sortOrder < minSortOrder) {
                  minSortOrder = sortOrder;
                }
              }
            }
            if (minSortOrder) {
              sortOrdersCategoryMap.set(resultCategory, minSortOrder);
            }
          }
        }

        if (sortOrdersCategoryMap.size) {
          resultCategories = resultCategories.sort((a, b) => this.compare(a, b, sortOrdersCategoryMap));
        }

        for (const resultCategory of resultCategories) {
          let data = task.resultCategories[resultCategory] as MobileResultViewModel[];
          if (data && sortOrdersMap.size) {
            data = data.sort((a, b) => this.compare(a, b, sortOrdersMap));
          }
          this.categoryDataSources[resultCategory] = data;
          this.initSelectionModel(data.filter((r) => r.selected));
        }

        this.workNavigationContext.task.ensureDefaultValues();

        for (const o of this.workNavigationContext.task.allResults) {
          const poc = this.persistenceService.getPendingObjectPackage(o);
          if (poc?.hasAnyChanges) {
            this.needForPersistence = true;
          }
        }
      }),
    );

    this.subscriptions.push(
      this.appService.dataService.pendingObjectsChanged$.subscribe((poc) => {
        if (poc.category === WorkConstants.PersistenceCategory_TaskDetail) {
          this.needForPersistence = poc.hasAnyChanges;
          this.changeDetectorRef.detectChanges();
        }
      }),
    );

    this.frameService.bottomSheetToggleCondition = (target?: string) =>
      this.ensureCurrentRequiredFieldsFilledIn(target);
  }

  private escCaptureHandler = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !this.allCurrentRequiredFieldsFilledIn()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  private compare<T>(a: T, b: T, sortOrdersMap: Map<T, number>): number {
    const avalue = sortOrdersMap?.get(a);
    const bvalue = sortOrdersMap?.get(b);
    if (avalue && bvalue) {
      return CompareHelper.compare(avalue, bvalue);
    } else if (avalue) {
      return 1;
    } else if (bvalue) {
      return -1;
    } else {
      return 0;
    }
  }

  async ngOnInit() {
    super.ngOnInit();
    this.res = await this.resAsync;
    super.updateSize();

    this.abcLoader = this.createAbcLoader();
    this.subscriptions.push(
      this.appService.displayLoader$.subscribe((value) => {
        value ? this.abcLoader.showLoader() : this.abcLoader.hideLoader();
      }),
    );

    if (this.workNavigationContext?.autoOpen) {
      this.autoExpandRequiredRow();
    }

    if (this.workNavigationContext?.lastExpandedElementId) {
      for (const categoryKey in this.categoryDataSources) {
        const rows = this.categoryDataSources[categoryKey];
        if (rows?.length) {
          const matching = rows.find((r) => r.id === this.workNavigationContext.lastExpandedElementId);
          if (matching) {
            this.expandedElement = this.getNewExpandedElement(false, null, matching);
            break;
          }
        }
      }
    }

    document.addEventListener('keydown', this.escCaptureHandler as any, { capture: true, passive: false } as any);
  }
  private async showMissingCurrentRequiredFieldsPopup(includeDuration: boolean = false): Promise<void> {
    if (this.okDialog.openDialogs.some((dialogRef) => dialogRef.componentInstance instanceof OkDialogComponent)) {
      return;
    }
    if (!this.isOkDialogOpened) {
      const dialogRef = this.okDialog.open(OkDialogComponent, {
        hasBackdrop: false,
        disableClose: true,
      });

      this.isOkDialogOpened = true;
      const okDialog: OkDialogComponent = dialogRef.componentInstance as OkDialogComponent;

      okDialog.onOkClick = () => {
        dialogRef.close();
        this.isOkDialogOpened = false;
      };

      const missingFields: string[] = new Array<string>();

      if (!this.isCurrentRequiredQuantityFilledIn()) {
        missingFields.push(this.res.get('Works-Task-Detail-Quantity'));
      }
      if (!this.isCurrentRequiredDescriptionFilledIn()) {
        missingFields.push(this.res.get('Works-Task-Detail-Description'));
      }
      if (!this.isCurrentRequiredPhotoInserted()) {
        missingFields.push(this.res.get('Works-Task-Detail-Photo'));
      }
      if (!this.isCurrentRequiredGeometryInserted()) {
        missingFields.push(this.res.get('Works-Task-Detail-Geometry'));
      }
      if (includeDuration) {
        missingFields.push(this.res.get('Works-Detail-Duration'));
      }
      let mFields: string = '<ul>';
      for (let index = 0; index < missingFields.length; index++) {
        mFields += '<li>' + missingFields[index] + '</li>';
      }
      mFields += '</ul>';

      okDialog.dialogHeader = this.res.get('Works-Task-Detail-MissingFieldsHeader');
      okDialog.dialogText = this.res.get('Works-Task-Detail-MissingFieldsText') + mFields;

      await dialogRef.afterClosed();
      this.isOkDialogOpened = false;
    }
  }

  private initSelectionModel(preSelectedObjects: IMobileResult[]): void {
    this.selection = new SelectionModel<IMobileResult>(true, preSelectedObjects);
    this.subscriptions.push(
      this.selection.changed.subscribe((e) => {
        if (e.added.length > 0) {
          e.added[0].selected = true;
        }
        if (e.removed.length > 0) {
          e.removed[0].selected = false;
        }
      }),
    );
  }

  private createAbcLoader(): AbcLoaderComponent {
    const LoaderAttr: ILoader = {
      id: 'loader-Save',
      value: 0,
      color: 'primary',
      mode: 'indeterminate',
      percentageValue: false,
    };
    return <AbcLoaderComponent>(
      this.abcContainer.createComponent(this.abcLoaderContainerRef, AbcLoaderComponent, LoaderAttr).componentRef
        .instance
    );
  }

  public revert(): void {
    for (const r of this.workNavigationContext.task.allResults) {
      this.workNavigationContext.task.revertChanges();
    }

    this.workNavigationContext.work.revertChanges();

    this.appService.dataService.revertPendingChanges(WorkConstants.PersistenceCategory_TaskDetail);
    this.appService.dataService.revertPendingChanges(WorkConstants.PersistenceCategory_WorkDetail);

    if (this.expandedResult) {
      this.expandedResult.revertDocuments();
    }

    this.needForPersistence = false;
    this.changeDetectorRef.detectChanges();
  }

  public getDesignation(result: MobileResultViewModel): string {
    return this.designationsMap?.get(result) ?? result?.designation;
  }

  public async mediaGalleryDataChanged(event: DataChangedEventArgs): Promise<void> {
    switch (event.type) {
      case DataChangedType.Added:
        for (let i = 0; i < event.documents.length; i++) {
          if (!event.documents[i].designation) {
            event.documents[i].designation = MTHelper.getUniqueDocumentName(
              event.documents[i].previewImageDataUrl.fileExtension,
              i,
            );
          }
        }
        const allResults = this.expandedResult.getAllManagedResults();
        const now = new Date();
        for (const result of allResults) {
          for (const document of event.documents) {
            let newDocVM: MobileDocumentViewModel;
            if (document.file) {
              newDocVM = result.addDocumentByFile(document.designation, now, document.file);
              newDocVM.readFile = async (file) => {
                const sizeMB = file.size / (1024 * 1024); // Bytes → MB
                if (sizeMB > 5)
                  this.uploadFileDocumentsTaskInfos.set(
                    newDocVM,
                    this.frame.loadingScreenService.addNewTask('Lade ' + newDocVM.designation + ' hoch...', true),
                  );
                if (!this.uploadFilesCache.has(file)) {
                  const data = (await ConvertHelper.readFileAsDataUrl(file)).base64;
                  this.uploadFilesCache.set(file, data);
                }
                return this.uploadFilesCache.get(file);
              };
              newDocVM.afterPersisted = () => {
                if (this.uploadFileDocumentsTaskInfos.has(newDocVM)) {
                  this.frame.loadingScreenService.finishTask(this.uploadFileDocumentsTaskInfos.get(newDocVM).id);
                }
              };
            } else {
              newDocVM = result.addDocumentByDataUrl(document.designation, now, document.previewImageDataUrl);
            }
          }
        }
        break;
    }
    this.needForPersistence = true;
  }

  private allRequiredFieldsFilledInByResult(result: MobileResultViewModel): boolean {
    return (
      this.isRequiredDescriptionFilledIn(result) &&
      this.isRequiredQuantityFilledIn(result) &&
      this.isRequiredPhotoInserted(result) &&
      this.isRequiredGeometryInserted(result)
    );
  }

  public allCurrentRequiredFieldsFilledIn(): boolean {
    return (
      this.isCurrentRequiredDescriptionFilledIn() &&
      this.isCurrentRequiredQuantityFilledIn() &&
      this.isCurrentRequiredPhotoInserted() &&
      this.isCurrentRequiredGeometryInserted()
    );
  }

  private onRowClick(event: MouseEvent, row: any): void {
    const targetElement = event?.target as HTMLElement;
    const isCheckbox = targetElement?.closest?.('abc-check-box');

    if (isCheckbox) {
      return;
    }
    this.expandedElement = this.expandedElement === row ? null : row;
  }

  public getNewExpandedElement(
    isCheckBox: boolean,
    currentResult: MobileResultViewModel,
    clickedResult: MobileResultViewModel,
  ): MobileResultViewModel {
    let ret = undefined;

    if (isCheckBox) {
      if (this.frame.mapInfo.isReadOnly) {
        return currentResult;
      }

      const isSameRow = currentResult === clickedResult;

      if (isSameRow) {
        if (!this.allCurrentRequiredFieldsFilledIn()) {
          this.showMissingCurrentRequiredFieldsPopup();
          return currentResult;
        }

        clickedResult.selected = false;
        this.expandedElement = null;
        return undefined;
      } else {
        if (!this.allCurrentRequiredFieldsFilledIn()) {
          this.showMissingCurrentRequiredFieldsPopup();
          return currentResult;
        }

        clickedResult.selected = !clickedResult.selected;

        if (clickedResult.selected) {
          if (this.allRequiredFieldsFilledInByResult(clickedResult)) {
            this.expandedElement = null;
            return undefined;
          } else {
            this.expandedElement = clickedResult;
            this.changeDetectorRef.detectChanges();
            this.onRowClick(new MouseEvent('click'), clickedResult);
          }
        }
        ret = clickedResult;
      }
    } else {
      if (currentResult === clickedResult) {
        if (!this.allCurrentRequiredFieldsFilledIn()) {
          this.showMissingCurrentRequiredFieldsPopup();
          return currentResult;
        }

        this.expandedElement = null;
        return undefined;
      } else {
        if (!this.allCurrentRequiredFieldsFilledIn()) {
          this.showMissingCurrentRequiredFieldsPopup();
          return currentResult;
        }

        ret = clickedResult;
      }
    }

    return ret;
  }

  public hasAnyRequiredFields(result: MobileResultViewModel): boolean {
    return (
      result.mtResultType.IsQuantityRequired ||
      result.mtResultType.IsDescriptionRequired ||
      result.mtResultType.IsPhotoRequired ||
      (result.taskAllowAnyGeometries && result.mtResultType.IsGeometryRequired)
    );
  }

  private ensureCurrentRequiredFieldsFilledIn(target?: string): boolean {
    if (this.openGeometryEditor) {
      return true;
    }

    if (this.frame.mapInfo.isReadOnly) {
      return true;
    }

    if (!this.allCurrentRequiredFieldsFilledIn()) {
      this.showMissingCurrentRequiredFieldsPopup();
      return false;
    }

    const durationMissing = this.isRequiredDurationMissing(target);
    if (durationMissing) {
      this.showMissingCurrentRequiredFieldsPopup(true);
      this.workNavigationContext.validateDurationOnOpen = true;
      if (target !== AppService.Route_WorkDetail) {
        setTimeout(() => this.worksService.navigateTo(AppService.Route_WorkDetail), 0);
        return false;
      }
      return true;
    }
    return true;
  }

  private isRequiredDurationMissing(target?: string): boolean {
    return (
      this.workNavigationContext?.work?.workTypeDurationModeValue === WorkTypeDurationMode.Erforderlich &&
      CompareHelper.isNullorFalse(this.workNavigationContext?.work?.duration) &&
      target !== AppService.Route_TaskDetail
    );
  }

  private isCurrentRequiredDescriptionFilledIn(): boolean {
    return this.isRequiredDescriptionFilledIn(this.expandedResult);
  }
  private isCurrentRequiredQuantityFilledIn(): boolean {
    return this.isRequiredQuantityFilledIn(this.expandedResult);
  }
  private isCurrentRequiredPhotoInserted(): boolean {
    return this.isRequiredPhotoInserted(this.expandedResult);
  }
  private isCurrentRequiredGeometryInserted(): boolean {
    return this.isRequiredGeometryInserted(this.expandedResult);
  }

  private isRequiredDescriptionFilledIn(result: MobileResultViewModel): boolean {
    if (result && result.selected) {
      if (result.mtResultType.IsDescriptionRequired) {
        if (!result.description) {
          return false;
        }
      }
    }
    return true;
  }

  private isRequiredQuantityFilledIn(result: MobileResultViewModel): boolean {
    if (result && result.selected) {
      if (result.mtResultType.IsQuantityRequired) {
        if (!result.quantity) {
          return false;
        }
      }
    }
    return true;
  }

  private isRequiredPhotoInserted(result: MobileResultViewModel): boolean {
    if (result && result.selected) {
      if (result.mtResultType.IsPhotoRequired) {
        if (result.documents.length === 0 && !result.photoImpossibleText) {
          return false;
        }
      }
    }
    return true;
  }

  private isRequiredGeometryInserted(result: MobileResultViewModel): boolean {
    if (result && result.selected) {
      if (result.taskAllowAnyGeometries && result.mtResultType.IsGeometryRequired) {
        return this.pointQuantity > 0 || this.lineQuantity > 0 || this.polygonQuantity > 0;
      }
    }
    return true;
  }

  public async save(): Promise<void> {
    try {
      if (this.ensureCurrentRequiredFieldsFilledIn()) {
        this.appService.callDisplayLoader(true);

        await this.appService.dataService.persistPendingChangesAsync(WorkConstants.PersistenceCategory_TaskDetail);
        await this.appService.dataService.persistPendingChangesAsync(WorkConstants.PersistenceCategory_WorkDetail);
        this.frame.map.clearSelections();
        this.needForPersistence = false;
      }
    } catch (error) {
      this.uploadFileDocumentsTaskInfos.forEach((v, k) => {
        this.frame.loadingScreenService.finishTask(v.id);
      });
      this.needForPersistence = true;
    } finally {
      this.appService.callDisplayLoader(false);
    }
  }

  public trackById(index, item) {
    return item.id;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.frameService.bottomSheetToggleCondition = undefined;
    this.appService.dispose();
    document.removeEventListener('keydown', this.escCaptureHandler as any, { capture: true, passive: false } as any);
  }

  private loadMediaDocuments(result: MobileResultViewModel): void {
    this.mediaDocuments = result.documents.map<MediaDocument>((mDoc) => {
      const mediaDoc = new MediaDocument();
      mediaDoc.designation = mDoc.designation;
      mediaDoc.previewImageDataUrl = mDoc.previewImageDataUrl;
      mediaDoc.downloadable = !mDoc.previewImageDataUrl && mDoc.kind === DocumentKind.Bild;
      mediaDoc.onDownloadButtonClick = async () =>
        (mDoc.previewImageDataUrl = new DataUrl(
          await this.appService.dataService.downloadDocumentThumb(mDoc.id),
          undefined,
          FileHelper.getExtension(mDoc.designation),
        ));
      return mediaDoc;
    });
  }

  public handleGeoWindow(geometryType: GeometryEditType): void {
    this.openGeometryEditor = true;
    let geometryEditor: GeometryEditorComponent;
    this.frame.lib.closeBottomSheet();

    if (this.expandedElement?.id) {
      this.workNavigationContext.lastExpandedElementId = this.expandedElement.id;
    }

    const style: WindowStyle = {
      height: 'auto',
      width: '347px',
      resizable: true,
      position: { my: 'top left', at: 'top left', offset: '60 10' },
    };

    const WindowAttr: IWindow = {
      id: 'geoReferenzeInterface',
      label: 'Geometrie erfassen',
      maximizeButton: false,
      minimizeButton: false,
      component: GeometryEditorComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    const window = this.abcContainer.createComponent(this.abcWindow, AbcWindowComponent, WindowAttr);
    const windowInstance: AbcWindowComponent = <AbcWindowComponent>window.componentRef.instance;
    windowInstance.createComponentInstance().subscribe((activeComponent) => {
      const w = activeComponent.openWindow();
      windowInstance.onAfterOpened.subscribe(() => {
        geometryEditor = w.instance as GeometryEditorComponent;

        if (this.workNavigationContext?.task?.id) {
          const taskGeometry = this.workNavigationContext.task.geometry;
          geometryEditor.editGeometry = taskGeometry;

          geometryEditor.triggerSave.subscribe(() => {
            this.workNavigationContext.task.geometry = geometryEditor.editGeometry;
            this.save();
          });

          geometryEditor.handleAfterSaveClick = () => {
            const sub = this.frame.lib.onViewStateChanged().subscribe((state) => {
              if (state.type === ViewStateChangeArgType.BottomSheetPanelOpened) {
                this.worksService.navigateTo(AppService.Route_TaskDetail);
              }
            });
            this.frame.lib.openBottomSheetByName(AppService.Widget_Works);
          };

          if (geometryType === GeometryEditType.Point) {
            setTimeout(() => {
              geometryEditor.toggleGeometryType('Point', 'MeasureCoord');
            }, 0);
          }

          if (geometryType === GeometryEditType.Line) {
            setTimeout(() => {
              geometryEditor.toggleGeometryType('LineString', 'MeasureCoord');
            }, 0);
          }

          if (geometryType === GeometryEditType.Polygon) {
            setTimeout(() => {
              geometryEditor.toggleGeometryType('Polygon', 'MeasureCoord');
            }, 0);
          }
        }

        geometryEditor.geometryType = geometryType;
        geometryEditor.closeGeoDialog.subscribe(() => {
          windowInstance.closeDialog();
          window.componentRef.destroy();
        });
      });
      windowInstance.onAfterClosed.subscribe(() => {
        geometryEditor?.clearVectorSource();
        this.openGeometryEditor = false;
        window.componentRef.destroy();
      });
    });
  }

  private autoExpandRequiredRow(): void {
    for (const categoryKey in this.categoryDataSources) {
      const rows = this.categoryDataSources[categoryKey];

      if (rows?.length) {
        for (const row of rows) {
          if (row.selected && this.hasAnyRequiredFields(row)) {
            this.expandedElement = this.getNewExpandedElement(false, null, row);
            this.changeDetectorRef.detectChanges();
            return;
          }
        }
      }
    }
  }

  public onSelectionCheckboxChanged(event: any, row: IMobileResult): void {
    if (!event.event) {
      return;
    }

    const isChecked = event.value;
    const current = this.expandedElement as MobileResultViewModel;
    const clicked = row as MobileResultViewModel;
    const newExpanded = this.getNewExpandedElement(true, current, clicked);
    if (newExpanded === current) {
      event.component.option('value', row.selected);
    } else {
      row.selected = isChecked;
      this.expandedElement = !newExpanded ? null : isChecked ? clicked : null;
      this.changeDetectorRef.detectChanges();
    }
  }
}
